import {useCallback, useRef, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { ToastSeverity, useToast } from 'thermia-react-container';
import { useRegistrationService } from '../service/registrationService';
import { InstallationConfig, OnsiteConfig } from '../types/onsiteConfig';
import { RegistrationsContextType } from './contextType';
import { useDevicesState } from './devicesState';
import { useRegistrationsState } from './registrationsState';

export function useAppState(): RegistrationsContextType {
  const [installationConfig, setInstallationConfig] = useState<InstallationConfig>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loading = useRef<boolean>(false);
  const { getRegistrationsConfig } = useRegistrationService();
  const { addToast } = useToast();
  const { t } = useTranslation('registrations');
  const { fetchDevices, devices, isLoading: isDevicesLoading, removeDevice, resetDevices } = useDevicesState();

  const {
    registrations,
    isLoading: isRegistrationsLoading,
    error: registrationsError,
    registrationsHasBeenFetched,
    completedHasBeenFetched,
    activeRegistration,
    isLoadingCompletedRegistrationsMetaData,
    completedRegistrationsMetaData,
    hasLoadedAnyCompletedRegistrationsMetaData,
    updateCustomer,
    updateLocation,
    updateDevices,
    updateSystemDescription,
    setActiveRegistration,
    fetchAllRegistrations,
    fetchRegistration,
    completeRegistration,
    createRegistration,
    deleteRegistrationAction,
    reopenCompletedRegistrationAction,
    fetchAllCompletedRegistrationsMetaData,
    removeActiveRegistration,
    resetRegistrations
  } = useRegistrationsState();

  const fetchInstallationConfig = useCallback(async () => {
    if (!loading.current && !installationConfig) {
      loading.current = true;
      setIsLoading(true);
      try {
        let response: OnsiteConfig = await getRegistrationsConfig();
        setInstallationConfig(response.installation);
      } catch (error) {
        addToast(t('registrations.general.errorSomethingWentWrongFetchUserData'), ToastSeverity.ERROR);
      }
      loading.current = false;
      setIsLoading(false);
    }
  }, [getRegistrationsConfig, isLoading, installationConfig, addToast, t]);

  return {
    config: {
      fetchInstallationConfig,
      installationConfig,
      isLoading
    },
    devices: {
      fetchDevices,
      devices,
      isDevicesLoading,
      removeDevice,
      resetDevices
    },
    registrations: {
      registrations,
      completedRegistrationsMetaData,
      registrationsHasBeenFetched,
      completedHasBeenFetched,
      activeRegistration,
      isRegistrationsLoading,
      isLoadingCompletedRegistrationsMetaData,
      registrationsError,
      hasLoadedAnyCompletedRegistrationsMetaData,
      updateCustomer,
      updateLocation,
      updateSystemDescription,
      updateDevices,
      setActiveRegistration,
      fetchAllRegistrations,
      fetchRegistration,
      completeRegistration,
      createRegistration,
      deleteRegistrationAction,
      reopenCompletedRegistrationAction,
      fetchAllCompletedRegistrationsMetaData,
      removeActiveRegistration,
      resetRegistrations
    }
  };
}
